import React, { Fragment, useCallback, useEffect, useMemo } from "react";
import classNames from "classnames";
import { get } from "../../../utils/dotutils";
import { loadComponent, saveComponent } from "../../../utils/components";
import ComponentConfig from "../../form/ComponentConfig";
import Collapse from "../../layout/Collapse";
import { empty } from "locutus/php/var";
import axios from "axios";
import debounce from "lodash-es/debounce";
import TextArea from "../../form/TextArea";
import Button from "../../form/Button";
import Modal from "src/components/layout/Modal";
import Portal from "src/components/layout/Portal";
import { connect } from "src/store";

type LabelButtonComponentPropsType = {
    componentConfig?: any,
    values?: any,
    labelOpened?: boolean|"always",
    labelLabel?: string,
    buttonPortal?: string,
};

const loadValuesRequests: {[key: string]: any} = {};
const saveValuesRequests: {[key: string]: any} = {};
const mountedLabels: {[key: string]: Array<(state: any) => void>} = {};

const LabelButtonComponent = (props: LabelButtonComponentPropsType) => {
    const component = get(props, "componentConfig.componentname");
    const id = get(props, "values." + get(props, "componentConfig.idfield"));

    const [isLoading, setIsLoading] = React.useState(false);
    const [isSaving, setIsSaving] = React.useState(false);
    const [values, setValues] = React.useState(null);
    useEffect(() => {
        mountedLabels[`${component}-${id}`] ??= [];
        mountedLabels[`${component}-${id}`].push(setValues);
        return () => {
            mountedLabels[`${component}-${id}`].splice(mountedLabels[`${component}-${id}`].indexOf(setValues), 1);
        }
    }, []);
    const [opened, setOpened] = React.useState(props.labelOpened === "always" ? true : props.labelOpened ?? false);
    useEffect(() => {
        if (props.labelOpened !== undefined) {
            setOpened(props.labelOpened === "always" ? true : props.labelOpened ?? false);
        }
    }, [props.labelOpened]);

    const saveChanges = useCallback(debounce((setValues, values, text: string, component: string, id: string | number) => {
        setIsSaving(true);
        if (saveValuesRequests[`${component}-${id}`]) {
            saveValuesRequests[`${component}-${id}`].aborted = true;
        }
        const saveRequest: any = saveValuesRequests[`${component}-${id}`] = saveComponent("Label", {
            ...values,
            text,
            component_type: component,
            component_id: id
        });
        saveRequest.then((response: any) => {
            if (saveRequest.aborted) {
                return;
            }
            delete saveValuesRequests[`${component}-${id}`];
            mountedLabels[`${component}-${id}`]?.forEach((_setValues) => {
                if (setValues === _setValues) return;
                _setValues(response.data);
            });
            if (!response.data) {
                // TODO error handling
                setIsSaving(false);
            } else {
                setIsSaving(false);
            }
        });
        saveRequest.catch((e: any) => {
            if (saveRequest.aborted) {
                return;
            }
            delete saveValuesRequests[`${component}-${id}`];
            if (!(e instanceof axios.Cancel) && e.message) {
                setIsSaving(false);
            }
        });
    }, 300), [setIsSaving, setIsLoading, setValues]);

    useEffect(() => {
        setIsLoading(true);
        if (component && id) {
            const loadValuesRequest = loadValuesRequests[`${component}-${id}`] ??= loadComponent("Label", {
                filter: [
                    { field: "component_type", value: component },
                    { field: "component_id", value: id }
                ]
            });
            loadValuesRequest.then((values: any) => {
                delete loadValuesRequests[`${component}-${id}`];
                setIsLoading(false);
                setValues(values);
            });
            loadValuesRequest.catch((e: any) => {
                delete loadValuesRequests[`${component}-${id}`];
                if (!(e instanceof axios.Cancel) && e.message) {
                    setIsLoading(false);
                    setValues(null);
                }
            });
            return () => {
                loadValuesRequest && loadValuesRequest.abort();
            }
        } else {
            setIsLoading(false);
            setValues(null);
        }
    }, [component, id]);
    
    if ([...(get(props, "LabelComponentConfig.config.disabled") || []), "Label"].indexOf(component) !== -1) {
        return null;
    }
    if (!(component && id)) {
        return null;
    }
    const text = get(values, "text");
    const button = <Button
        color={empty(text) ? "default" : "warning"}
        icon={{ lib: "fas", name: "sticky-note" }}
        tooltip={(empty(text) ? (props.labelLabel ?? "Címke/Megjegyzés") : <div>{text.split('\n').map((item: string, key: number) => {
            return <Fragment key={key}>{item}<br /></Fragment>
        })}</div>) as any}
        className={classNames({ opened: opened, loading: isLoading, "animated jello infinite": isSaving })}
        onClick={() => {
            setOpened(!opened);
        }}
    />;
    const field = <div className="form-group">
        <TextArea
            name="text"
            value={text}
            disabled={isLoading}
            label={props.labelLabel ?? "Címke/Megjegyzés"}
            saveChanges={(field: string, value: any) => {
                saveChanges(setValues, values, value, component, id);
                setValues({ ...(values || {}), [field]: value });
            }}
        />
    </div>;
    if (props.labelOpened === "always") {
        return <div className="form-group label-form">{field}</div>;
    }
    return (
        <div className="form-group label-form">
            <Collapse collapsed={!!opened} button={
                props.buttonPortal ? <Portal portalId={props.buttonPortal}>{button}</Portal> : button
            }>
                <div className={classNames("label-text", { opened: opened, loading: isLoading, saving: isSaving })}>
                    {field}
                </div>
            </Collapse>
        </div>
    );
}

export const LabelButton = connect((state) => ({partner: state.app.user.partner}))((props: LabelButtonComponentPropsType) => {
    if ((props as any).partner) return null;
    return (
        <ComponentConfig
            componentName="Label"
            componentConfigAs="LabelComponentConfig"
        >
            <LabelButtonComponent {...props} />
        </ComponentConfig>
    );
});

// openedInfo: boolean,
// infoContent: string | null

class InfoButton extends React.Component<any, any> {
    state: any = {
        isLoading: false,
        isSaving: false,
    }

    componentDidMount(): void {
        this.getComponentInfo();
    }

    getComponentInfo = () => {
        if (this.props.componentEditor) {
            const request = loadComponent("Documentation", {
                filter: [{field: "component", value: this.props.componentEditor},
                    {field: "field", operator: "isnull"}]
            });
            request.then((response) => {
                if (response.id) {
                    this.setState({"infoContent": response.text})
                }
            });
        }
    }

    render() {

        return (
            <>
                {this.state.infoContent ? <><Modal
                    size="large"
                    state={this.state.openedInfo}
                    header="Dokumentáció"
                    buttons={[
                        {
                            text: 'Bezárás',
                            props: { color: "default" },
                            onClose: true,
                            onClick: async () => {
                                try {
                                    this.setState({ openedInfo: false });
                                } catch (e) {
                                    console.log(e)
                                }
                            }
                        }
                    ]}
                >
                    <div dangerouslySetInnerHTML={{ __html: this.state.infoContent }} />
                </Modal>
                    <Portal portalId={this.props.buttonPortal}><Button
                        color={"default"}
                        icon={{ lib: "fa", name: "info" }}
                        tooltip={"Dokumentáció"}
                        onClick={() => { this.setState({ openedInfo: true }) }}
                    /> </Portal></> : null}
            </>
        )
    }
}


// TODO a ref-et szépen ahogy azt kéne, meg kell majd csinálni, csak érteni is kéne hogy mi történik.
export default <FormType extends {} = any,>(SubForm: FormType): FormType => {
    // @ts-ignore
    return React.forwardRef((props: any, _ref) => {
        const infoButtonPortal = useMemo(() => "btn" + new Date().getTime(), []);
        const labelButtonPortal = useMemo(() => "label" + new Date().getTime(), []);
        let ref = _ref;
        if (_ref instanceof Function) {
            ref = (form: any) => {
                if (form) {
                    form.refresh = () => {
                        props.reloadForm();
                    };
                    form.refreshBackground = () => {
                        props.reloadForm();
                    };
                }
                _ref(form);
            }
        }
        // @ts-ignore
        const Form = <SubForm ref={ref} {...props} />;
        return <Fragment>
            <div className="formLabelContainer">
                <div id={infoButtonPortal} />
                <div id={labelButtonPortal} />
            </div>
            <InfoButton {...props} buttonPortal={infoButtonPortal} />
            <LabelButton {...props} buttonPortal={labelButtonPortal} />
            {Form}
        </Fragment>;
    });
}
